/**
 * PageContextMenu — right-click context menu for a page row in the
 * workspace sidebar. Mirrors the Notion sidebar context menu:
 *
 *   - Rename            (inline edit; handled by the caller)
 *   - Add sub-page      (opens the existing CreatePageDialog)
 *   - Move to…          (opens MovePageDialog with a page-tree picker)
 *   - Move to workspace… (FRIDAY-34; workspace owners and admins only)
 *   - Copy link         (writes the SPA URL to the clipboard)
 *   - Duplicate         (creates a sibling copy via the create endpoint)
 *   - Archive / Restore (toggles archivedAt)
 *   - Delete            (hard-delete; owner only — server enforces)
 *
 * Rendered via React portal to `document.body` so the fixed-positioned
 * menu escapes the sidebar's overflow context and never gets clipped
 * by a scrollable parent.
 *
 * Positioning: fixed at the mouse coordinates, with edge-clamping so
 * the menu never spills off the viewport. Closes on outside click,
 * scroll, Escape, or after a successful action.
 */

import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { cn } from '@/lib/utils'

export interface PageContextMenuProps {
  /** Anchor in viewport coords; null = menu closed. */
  anchor: { x: number; y: number } | null
  pageTitle: string
  isArchived: boolean
  onClose: () => void
  onRename: () => void
  onAddSubPage: () => void
  onMove: () => void
  /** FRIDAY-34: shown only when the caller may move pages out of the workspace. */
  onMoveToWorkspace?: () => void
  onCopyLink: () => void
  onDuplicate: () => void
  onArchive: () => void
  onRestore: () => void
  onDelete: () => void
}

const VIEWPORT_GUTTER = 8
const MENU_WIDTH = 220
const MENU_MAX_HEIGHT = 360

export function PageContextMenu(props: PageContextMenuProps) {
  const { anchor, onClose, pageTitle, isArchived } = props
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  useLayoutEffect(() => {
    if (!anchor) {
      setPos(null)
      return
    }
    const vw = window.innerWidth
    const vh = window.innerHeight
    const left = Math.min(anchor.x, vw - MENU_WIDTH - VIEWPORT_GUTTER)
    const top = Math.min(anchor.y, vh - MENU_MAX_HEIGHT - VIEWPORT_GUTTER)
    setPos({ top: Math.max(VIEWPORT_GUTTER, top), left: Math.max(VIEWPORT_GUTTER, left) })
  }, [anchor])

  useEffect(() => {
    if (!anchor) return
    const onScroll = () => onClose()
    window.addEventListener('scroll', onScroll, true)
    return () => window.removeEventListener('scroll', onScroll, true)
  }, [anchor, onClose])

  // Keyboard support: focus the first item on open, Escape closes (even
  // when opened by pointer and focus is outside the menu), arrows move.
  const onCloseRef = useRef(onClose)
  onCloseRef.current = onClose
  const isOpen = anchor !== null
  useEffect(() => {
    if (!isOpen) return
    const items = () => Array.from(menuRef.current?.querySelectorAll<HTMLButtonElement>('[role="menuitem"]') ?? [])
    // preventScroll: a focus-induced scroll would trip the close-on-scroll handler.
    items()[0]?.focus({ preventScroll: true })
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        e.stopPropagation()
        onCloseRef.current()
      } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        const list = items()
        if (list.length === 0) return
        e.preventDefault()
        const i = list.indexOf(document.activeElement as HTMLButtonElement)
        const next = e.key === 'ArrowDown' ? (i + 1) % list.length : (i - 1 + list.length) % list.length
        list[next]?.focus()
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [isOpen, anchor?.x, anchor?.y])

  if (!anchor) return null

  return createPortal(
    <>
      <div
        className="fixed inset-0 z-[60]"
        onClick={onClose}
        onContextMenu={(e) => {
          e.preventDefault()
          onClose()
        }}
      />
      <div
        ref={menuRef}
        role="menu"
        aria-label={`Actions for ${pageTitle}`}
        className={cn(
          'z-[70] overflow-hidden rounded-md border border-surface-200 bg-white py-1 shadow-xl shadow-black/10',
          'dark:border-surface-700 dark:bg-surface-800',
        )}
        style={{
          position: 'fixed',
          top: pos?.top ?? anchor.y,
          left: pos?.left ?? anchor.x,
          width: MENU_WIDTH,
          maxHeight: MENU_MAX_HEIGHT,
          overflowY: 'auto',
        }}
      >
        <div className="truncate px-3 py-1.5 text-[10px] uppercase tracking-wider text-surface-400">
          {pageTitle}
        </div>

        <Item icon="✎" label="Rename" onClick={() => { props.onRename(); onClose() }} />
        <Item icon="＋" label="Add sub-page" onClick={() => { props.onAddSubPage(); onClose() }} />
        <Item icon="⤴" label="Move to…" onClick={() => { props.onMove(); onClose() }} />
        {props.onMoveToWorkspace && (
          <Item
            icon="⇄"
            label="Move to workspace…"
            onClick={() => { props.onMoveToWorkspace?.(); onClose() }}
          />
        )}
        <Item icon="⧉" label="Duplicate" onClick={() => { props.onDuplicate(); onClose() }} />
        <Item icon="🔗" label="Copy link" onClick={() => { props.onCopyLink(); onClose() }} />
        <Sep />
        {isArchived ? (
          <Item icon="↺" label="Restore" onClick={() => { props.onRestore(); onClose() }} />
        ) : (
          <Item icon="🗄" label="Archive" onClick={() => { props.onArchive(); onClose() }} />
        )}
        <Item icon="✕" label="Delete" onClick={() => { props.onDelete(); onClose() }} danger />
      </div>
    </>,
    document.body,
  )
}

function Item({
  icon,
  label,
  onClick,
  danger,
}: {
  icon: string
  label: string
  onClick: () => void
  danger?: boolean
}) {
  return (
    <button
      type="button"
      role="menuitem"
      onClick={onClick}
      className={cn(
        'flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs hover:bg-surface-100 dark:hover:bg-surface-700',
        danger && 'text-red-600 hover:bg-red-50 dark:text-red-300 dark:hover:bg-red-900/30',
        !danger && 'text-surface-700 dark:text-surface-200',
      )}
    >
      <span className="w-4 text-center text-sm leading-none">{icon}</span>
      <span className="flex-1">{label}</span>
    </button>
  )
}

function Sep() {
  return <div className="my-1 h-px bg-surface-200 dark:bg-surface-700" />
}
